'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import {
  isPushSupported,
  getNotificationPermission,
  requestNotificationPermission,
  saveNotificationPreferences,
  getNotificationPreferences,
  defaultNotificationPreferences,
  sendPushNotification,
} from '@/lib/pushNotifications';

export default function PushNotificationManager() {
  const [permission, setPermission] = useState<string>('default');
  const [showBanner, setShowBanner] = useState(false);
  const [prefs, setPrefs] = useState(defaultNotificationPreferences);
  const [showPrefs, setShowPrefs] = useState(false);
  const [testSent, setTestSent] = useState(false);

  useEffect(() => {
    if (!isPushSupported()) return;
    const perm = getNotificationPermission();
    setPermission(perm);
    const dismissed = localStorage.getItem('pushBannerDismissed');
    const savedPrefs = getNotificationPreferences();
    if (Object.keys(savedPrefs)?.length > 0) {
      setPrefs({ ...defaultNotificationPreferences, ...savedPrefs });
    }
    if (perm === 'default' && !dismissed) {
      const timer = setTimeout(() => setShowBanner(true), 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleEnable = async () => {
    const granted = await requestNotificationPermission();
    setPermission(granted ? 'granted' : 'denied');
    setShowBanner(false);
    if (granted) {
      saveNotificationPreferences(prefs);
      sendPushNotification({
        title: '🔔 Notifications Enabled!',
        body: 'You will now receive booking alerts and travel updates from Dial For Flight.',
        tag: 'welcome',
      });
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pushBannerDismissed', 'true');
  };

  const handleSavePrefs = () => {
    saveNotificationPreferences(prefs);
    setShowPrefs(false);
  };

  const handleTestNotification = () => {
    sendPushNotification({
      title: '✅ Test Notification',
      body: 'Push notifications are working correctly for Dial For Flight!',
      tag: 'test',
    });
    setTestSent(true);
    setTimeout(() => setTestSent(false), 3000);
  };

  if (!isPushSupported()) return null;

  return (
    <>
      {/* Permission Banner */}
      {showBanner && permission === 'default' && (
        <div className="fixed bottom-32 left-4 right-4 sm:left-auto sm:right-6 sm:w-96 z-50 bg-white rounded-2xl shadow-2xl border border-border p-5 animate-slide-in">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
              <Icon name="BellIcon" size={24} className="text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-black text-foreground text-base">Stay Updated!</h3>
              <p className="text-sm text-muted-foreground mt-1">Get instant alerts for booking confirmations, price drops, and travel updates.</p>
            </div>
            <button onClick={handleDismiss} className="text-muted-foreground hover:text-foreground">
              <Icon name="XMarkIcon" size={20} />
            </button>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleEnable}
              className="flex-1 bg-gradient-to-r from-primary to-secondary text-white font-bold py-2.5 rounded-xl hover:shadow-lg transition-all text-sm"
            >
              Enable Notifications
            </button>
            <button
              onClick={handleDismiss}
              className="px-4 py-2.5 border-2 border-border text-foreground font-bold rounded-xl hover:border-primary transition-colors text-sm"
            >
              Later
            </button>
          </div>
        </div>
      )}
      {/* Preferences Modal */}
      {showPrefs && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-black text-xl text-foreground">Notification Preferences</h2>
              <button onClick={() => setShowPrefs(false)} className="text-muted-foreground hover:text-foreground">
                <Icon name="XMarkIcon" size={24} />
              </button>
            </div>
            <div className="space-y-4">
              {Object.entries(prefs)?.map(([key, value]) => (
                <label key={key} className="flex items-center justify-between cursor-pointer">
                  <span className="text-sm font-bold text-foreground capitalize">
                    {key?.replace(/([A-Z])/g, ' $1')?.trim()}
                  </span>
                  <div
                    onClick={() => setPrefs({ ...prefs, [key]: !value })}
                    className={`w-12 h-6 rounded-full transition-colors cursor-pointer relative ${
                      value ? 'bg-primary' : 'bg-gray-200'
                    }`}
                  >
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      value ? 'translate-x-7' : 'translate-x-1'
                    }`} />
                  </div>
                </label>
              ))}
            </div>
            {permission === 'granted' && (
              <button
                onClick={handleTestNotification}
                className="w-full mt-4 border-2 border-primary text-primary font-bold py-2.5 rounded-xl hover:bg-primary hover:text-white transition-colors text-sm"
              >
                {testSent ? '✅ Test Sent!' : 'Send Test Notification'}
              </button>
            )}
            <button
              onClick={handleSavePrefs}
              className="w-full mt-3 bg-gradient-to-r from-primary to-secondary text-white font-bold py-3 rounded-xl hover:shadow-lg transition-all"
            >
              Save Preferences
            </button>
          </div>
        </div>
      )}
    </>
  );
}
